import {
  Button,
  composeRenderProps,
  type GridListItemProps,
  type GridListProps,
  GridList as RACGridList,
  GridListItem as RACGridListItem,
} from "react-aria-components";
import { tv } from "tailwind-variants";

import { Checkbox } from "@/components/ui/checkbox";
import { composeTailwindRenderProps } from "@/components/ui/utils";

const gridListStyles = tv({
  base: "relative overflow-auto rounded-md border p-1",
});

const gridListItemStyles = tv({
  base: [
    "relative -mb-px flex cursor-default select-none gap-3 rounded-md px-2 py-1.5 text-sm outline-0",
    "not-last:mb-0.5",
  ],
  variants: {
    isHovered: {
      true: "bg-accent",
    },
    isSelected: {
      true: "z-20",
    },
    isDisabled: {
      true: "opacity-50",
    },
    isFocusVisible: {
      true: ["outline", "outline-2", "-outline-offset-2", "outline-ring/70"],
    },
  },
});

export function GridList<T extends object>({
  children,
  ...props
}: GridListProps<T>) {
  return (
    <RACGridList
      {...props}
      className={composeTailwindRenderProps(props.className, gridListStyles())}
    >
      {children}
    </RACGridList>
  );
}

export function GridListItem({ children, ...props }: GridListItemProps) {
  const textValue = typeof children === "string" ? children : props.textValue;

  return (
    <RACGridListItem
      {...props}
      textValue={textValue}
      className={composeRenderProps(props.className, (className, renderProps) =>
        gridListItemStyles({ className, ...renderProps }),
      )}
    >
      {(renderProps) =>
        typeof children === "function" ? (
          children(renderProps)
        ) : (
          <>
            {/* Add elements for drag and drop and selection. */}
            {renderProps.allowsDragging && <Button slot="drag">≡</Button>}
            {renderProps.selectionMode === "single" && (
              <Checkbox slot="selection" />
            )}
            {renderProps.selectionMode === "multiple" &&
              renderProps.selectionBehavior === "toggle" && (
                <Checkbox slot="selection" />
              )}
            {children}
          </>
        )
      }
    </RACGridListItem>
  );
}
